import { RoutePath, pathHashbang } from '../routes/constants';
import { PlainRoute } from 'react-router';
import { DriveItemListOrigin } from '../containers/DriveItemListPage/constants';
import hashHistory from 'react-router/lib/hashHistory';
import { DriveItemOrigin } from '../containers/DriveItemList/actions';
import { RouterParamsState } from '../containers/SetRouteParams/reducer';

const ROUTE_PAGE_CLASS_MAP: { [key in RoutePath]?: string } = {
	[RoutePath.Home]: 'page-home',
	[RoutePath.ViewDriveItem]: 'page-view',
	[RoutePath.Authentication]: 'page-authentication',
	[RoutePath.ParametersPage]: 'page-parameters',
	[RoutePath.ProfilePage]: 'page-profile',
	[RoutePath.DriveItemListPage]: 'page-list',
	[RoutePath.TheaterList]: 'page-theater-list',
	[RoutePath.Theater]: 'page-theater',
	[RoutePath.SharePage]: 'page-share',
	[RoutePath.DriveItemDetailPage]: 'page-detail',
	[RoutePath.StaticPage]: 'page-static',
	[RoutePath.Editor]: 'page-editor',
	[RoutePath.ProposalStatsPage]: 'page-proposal-stats',
	[RoutePath.NotesPage]: 'page-notes',
	[RoutePath.NotFound]: 'page-not-found',
};

class RoutesUtils {
	// paths
	public static historyPush(path: string) {
		hashHistory.push(path + hashHistory.getCurrentLocation().search);
	}

	// paths
	public static getPathDefault(keepHashbang: boolean = false) {
		return this.replaceHashbang(RoutePath.Default, keepHashbang);
	}

	public static getPathHome(keepHashbang: boolean = false) {
		return this.replaceHashbang(RoutePath.Home, keepHashbang);
	}

	public static getPageNotFound() {
		return hashHistory.replace('error404');
	}

	public static getPathStaticPage(driveItemId: string, keepHashbang: boolean = false) {
		let path = RoutePath.StaticPage.replace(':component', driveItemId);
		return this.replaceHashbang(path, keepHashbang);
	}

	public static getPathAuthentication(keepHashbang: boolean = false) {
		return this.replaceHashbang(RoutePath.Authentication, keepHashbang);
	}

	public static getPathParameters(keepHashbang: boolean = false) {
		return this.replaceHashbang(RoutePath.ParametersPage, keepHashbang);
	}

	public static getPathProfile(keepHashbang: boolean = false) {
		return this.replaceHashbang(RoutePath.ProfilePage, keepHashbang);
	}

	public static getPathProposalStats(keepHashbang: boolean = false) {
		return this.replaceHashbang(RoutePath.ProposalStatsPage, keepHashbang);
	}

	public static getPathDriveItemListPage(origin: DriveItemListOrigin, originParam: string | null, keepHashbang: boolean = false) {
		let path = RoutePath.DriveItemListPage.replace(':origin', origin);

		const originParamReplaceValue = originParam ? `/${originParam}` : '';
		path = path.replace('(/:originParam)', originParamReplaceValue);

		return this.replaceHashbang(path, keepHashbang);
	}

	public static getPathDriveItemDetailPage(driveItemId: number, folderId?: number, keepHashbang: boolean = false) {
		let path = RoutePath.DriveItemDetailPage.replace(':driveItemId', driveItemId.toString());

		const folderIdParamReplaceValue = folderId ? `/${folderId}` : '';
		path = path.replace('(/:folderId)', folderIdParamReplaceValue);

		return this.replaceHashbang(path, keepHashbang);
	}

	public static getPathTheater(origin: DriveItemListOrigin | DriveItemOrigin, originParam: string | null, driveItemId: number, keepHashbang: boolean = false) {
		let path;
		if (origin === DriveItemOrigin.Menu) {
			path = RoutePath.Theater
				.replace(':driveItemId', driveItemId.toString());
		} else {
			path = RoutePath.TheaterList
				.replace(':origin', origin)
				.replace(':driveItemId', driveItemId.toString());

			const originParamReplaceValue = originParam ? `/${originParam}` : '';
			path = path.replace('(/:originParam)', originParamReplaceValue);
		}

		return this.replaceHashbang(path, keepHashbang);
	}

	public static getPathEditor(clientId: number, resourceId: number): string {
		return `editor/${clientId}/${resourceId}`;
	}

	public static getPathNotes(keepHashbang: boolean = false) {
		return this.replaceHashbang(RoutePath.NotesPage, keepHashbang);
	}

	public static doesRoutesContainsPath = (path: string, routes?: PlainRoute[]): boolean => {
		return !!routes && routes.some(route => route.path === path);
	}

	static isGalleryRoot = (routerParamsHistory: RouterParamsState[]): boolean => {
		const currentRoute = RoutesUtils.getCurrentRoute(routerParamsHistory);
		const currentOrigin = RoutesUtils.getCurrentOrigin(routerParamsHistory);
		const currentOriginParam = RoutesUtils.getCurrentOriginParam(routerParamsHistory);

		return currentRoute === RoutePath.DriveItemListPage && currentOrigin === DriveItemListOrigin.Folder && !currentOriginParam;
	}

	public static currentLocationIs(routerParamsHistory: RouterParamsState[], location: RoutePath | string): boolean {
		const currentRoute = RoutesUtils.getCurrentRoute(routerParamsHistory);
		const currentOrigin = RoutesUtils.getCurrentOrigin(routerParamsHistory);

		return location === currentRoute || location === currentOrigin;
	}

	public static getCurrentRoute = (routerParamsHistory: RouterParamsState[]): RoutePath => {
		const currentRoute = RoutesUtils.getCurrentRouterParam(routerParamsHistory);
		const { routes } = currentRoute;
		const route = routes[routes.length - 1].path as RoutePath;

		return route;
	}

	public static getCurrentOrigin = (routerParamsHistory: RouterParamsState[]): string => {
		const currentRoute = RoutesUtils.getCurrentRouterParam(routerParamsHistory);
		const { params: { origin } } = currentRoute;

		return origin;
	}

	public static getCurrentOriginParam = (routerParamsHistory: RouterParamsState[]): string => {
		const currentRoute = RoutesUtils.getCurrentRouterParam(routerParamsHistory);
		const { params: { originParam } } = currentRoute;
		return originParam;
	}

	public static getCurrentRouterParam = (routerParamsHistory: RouterParamsState[]): RouterParamsState => {
		return routerParamsHistory[routerParamsHistory.length - 1];
	}

	public static getAppInnerContainerPageClasses = (routerParamsHistory: RouterParamsState[]): string => {
		if (!routerParamsHistory || routerParamsHistory.length === 0) {
			return '';
		}

		const currentRoute = RoutesUtils.getCurrentRoute(routerParamsHistory);
		const { params } = RoutesUtils.getCurrentRouterParam(routerParamsHistory);
		let classes = '';

		const pageClass = ROUTE_PAGE_CLASS_MAP[currentRoute];
		if (pageClass) {
			classes += ` ${pageClass}`;
		}

		if (currentRoute === RoutePath.StaticPage && params.component) {
			classes += ` component-${params.component}`;
		}

		if (params.driveItemId) {
			classes += ` driveItem-${params.driveItemId}`;
		}

		return classes;
	}

	public static redirectToParentDir = (parentId: number): void => {
		RoutesUtils.historyPush(RoutesUtils.getPathDriveItemListPage(DriveItemListOrigin.Folder, parentId.toString()));
	}

	private static replaceHashbang(path: string, keepHashbang: boolean): string {
		const replaceValue = keepHashbang ? '!/' : '';
		return path.replace(pathHashbang, replaceValue);
	}
}

export const routesUtils = new RoutesUtils;

export default RoutesUtils;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/routes.ts